// My Calendar — a My Desk panel (owner, 2026-09-10 / "build it" 2026-09-18;
// redesigned 2026-10-11 after "the other did not look good"): the signed-in
// person's own Google Calendar, read-only.
//
// Layout: "Today" first (always shown — "Nothing on your calendar today"
// when it is empty), then the next 7 days grouped by day. Each row: the
// time range on the left (an "All day" pill for all-day events), the title,
// and the location muted. A today event that has already ended reads
// quieter. Rows open the event in Google Calendar for the signed-in account
// (`authuser`, same idea as Nav.tsx's calendar mark).
//
// TIME: read at the VIEWER'S OWN clock (browser local time), never the
// clinic clock — a personal calendar means nothing translated into a
// clinic's time zone (lib/myGoogle.ts calendarSections).
//
// States, all named, never blank: loading, viewing-as (403, P2 — see
// MyMailPanel.tsx), fault, not connected, and the empty Today line.
import type { JSX } from 'preact';
import { useResource } from '../lib/store';
import { formatTimeOfDay } from '../lib/date';
import { session } from '../lib/session';
import {
  MY_CALENDAR_KEY, fetchMyCalendar, reloadMyCalendar, calendarSections, upcomingDayHeading,
  VIEWING_AS_BLOCKED_MESSAGE, type MyCalendarData, type MyCalendarSectionEvent,
} from '../lib/myGoogle';
import { calendarUrlFor, withGoogleAccount } from './Nav';
import { DeskPanel } from './DeskPanel';
import { FaultState } from './FaultState';
import { ReloadButton } from './ReloadButton';

function safeTime(iso: string): string {
  try { return formatTimeOfDay(iso); } catch { return ''; }
}

function EventRow({ event, email }: { event: MyCalendarSectionEvent; email: string | null }): JSX.Element {
  const when = event.allDay
    ? <span class="mycal-allday">All day</span>
    : <span class="mycal-range">{safeTime(event.start)}<span class="mycal-dash"> – </span>{safeTime(event.end)}</span>;
  const body = (
    <>
      <span class="mycal-when">{when}</span>
      <span class="mycal-what">
        <span class="mycal-title">{event.title || '(no title)'}</span>
        {event.location ? <span class="mycal-loc">{event.location}</span> : null}
      </span>
    </>
  );
  const cls = `mycal-row${event.past ? ' mycal-past' : ''}`;
  return (
    <li>
      {event.url ? (
        <a class={cls} href={withGoogleAccount(event.url, email)} target="_blank" rel="noopener noreferrer" data-testid={`my-calendar-row-${event.id}`}>
          {body}
        </a>
      ) : (
        <div class={cls} data-testid={`my-calendar-row-${event.id}`}>{body}</div>
      )}
    </li>
  );
}

export function MyCalendarPanel(
  { chromeless = false }: { chromeless?: boolean } = {},
): JSX.Element {
  const load = useResource<MyCalendarData>(MY_CALENDAR_KEY, fetchMyCalendar);
  const email = session.value.me?.email ?? null;

  const logo = <span class="calendar-mark" role="img" aria-label="Google Calendar" data-testid="my-calendar-logo" />;
  const openCal = (
    <a class="mycal-open" href={calendarUrlFor(email)} target="_blank" rel="noopener noreferrer" data-testid="my-calendar-open">
      Open Calendar
    </a>
  );
  const toolbar = (
    <ReloadButton
      label="Reload calendar"
      testId="my-calendar-refresh"
      leading={<span class="panel-lead mycal-lead">{logo}{openCal}</span>}
      onClick={() => { void reloadMyCalendar(); }}
    />
  );
  const wrap = (body: JSX.Element): JSX.Element => (
    <DeskPanel title="My Calendar" testId="my-calendar-panel" chromeless={chromeless} className="mycal">
      {body}
    </DeskPanel>
  );

  if (load.status === 'loading') {
    return wrap(<p class="muted mycal-state" data-testid="my-calendar-loading">Loading your calendar.</p>);
  }

  if (load.status === 'fault') {
    if (load.error === VIEWING_AS_BLOCKED_MESSAGE) {
      return wrap(<p class="muted mycal-state" data-testid="my-calendar-viewing-as">Hidden while you are viewing as someone else.</p>);
    }
    return wrap(
      <>
        {toolbar}
        <FaultState name="my_calendar_load_failed" detail={`${load.error} — Google Calendar did not answer. Try the reload circle.`} />
      </>,
    );
  }

  const { data } = load;

  if (!data.connected) {
    return wrap(
      <>
        {toolbar}
        <p class="muted mycal-state" data-testid="my-calendar-not-connected">
          Calendar is not connected for this account. Your calendar opens with Open Calendar.
        </p>
      </>,
    );
  }

  const { today, upcoming, todayKey } = calendarSections(data.events);

  return wrap(
    <>
      {toolbar}
      <div class="mycal-list" data-testid="my-calendar-queue">
        <section class="mycal-day" data-testid="my-calendar-today">
          <h3 class="mycal-heading mycal-today">Today</h3>
          {today.length ? (
            <ul>{today.map((e) => <EventRow key={e.id} event={e} email={email} />)}</ul>
          ) : (
            <p class="muted mycal-none" data-testid="my-calendar-empty">Nothing on your calendar today</p>
          )}
        </section>
        {upcoming.map((group) => (
          <section class="mycal-day" key={group.key} data-testid={`my-calendar-day-${group.key}`}>
            <h3 class="mycal-heading">{upcomingDayHeading(group.key, todayKey)}</h3>
            <ul>{group.events.map((e) => <EventRow key={e.id} event={e} email={email} />)}</ul>
          </section>
        ))}
        {!upcoming.length ? (
          <p class="muted mycal-none" data-testid="my-calendar-week-empty">Nothing else in the next 7 days.</p>
        ) : null}
      </div>
    </>,
  );
}
